import React, { useState, useEffect, useRef } from 'react';
import { JBAlert, JBButton, JBTypography, JBCodeInput } from '@jbhi-fi/lanyard-ui';
import ReceiptLayout from '../receipt.layout';
import CustomerHelper from '../../customers/helpers/CustomerHelper';
import { useReceiptContext } from '../context/receipt.context';
import { getHashedCustomerEmail } from '../../../../utils/event-tracking/event-data';
import { useTrackingContext, trackingContext, formAction } from '@jbhi-fi/components/src/Tracking';
import ReceiptHelper from '../helpers/receipt.helper';
import { useSubscribe } from '@jbhi-fi/components/src/hooks/useKlaviyoSubscribe';

const receiptHelper = new ReceiptHelper();
const customerHelper = new CustomerHelper();

const validationMessages = {
  required: 'That code is invalid. All 4 digits are required.',
  invalid: "That code is invalid. Give it another go or tap 'Resend'.",
};
const LINK_ERROR_HEADING = 'Unable to link receipt';
const LINK_ERROR_CONTENT = 'We were unable to link your receipt, please contact customer service.';

const VerifyView = ({ receiptId, history }) => {
  const { data, isLoading, setVerifyData, clearSession, isLinked } = useReceiptContext();
  const { tracker } = useTrackingContext();
  const inputBlock = useRef(null);
  const [linkError, setLinkError] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [code, setCode] = useState(null);
  const [validationError, setValidationError] = useState(validationMessages.required);

  const { onSubscribe } = useSubscribe();

  // Used with key on JBCodeInput as a hack to force remount (reset) of component on change
  const [codeInputKey, setCodeInputKey] = useState(1);

  useEffect(() => {
    if (receiptId && receiptId === data?.receipt?.ReceiptData?.Code) {
      if (!data.verify) {
        history.push(`/${receiptId}`);
      } else if (isLinked || data.verify?.raw?.data?.phone) {
        history.push(`/${receiptId}/already-claimed`);
      } else if (
        data.receipt.ErrorCode &&
        (data.receipt.ErrorCode === 'InvalidReceiptCode' || data.receipt.ErrorCode === 'InvalidData')
      ) {
        history.push(`/`);
      }
    }
  }, [data]);

  useEffect(() => {
    tracker.group = 'enter otp';
    tracker.extraData = { ...tracker.extraData, hashedEmail: getHashedCustomerEmail(), data: { value: receiptId } };
    tracker.trackFormEvent({ action: formAction.stepView });
  }, []);

  useEffect(() => {
    if (linkError) {
      tracker.trackError({
        errorMsg: `${LINK_ERROR_HEADING}. ${LINK_ERROR_CONTENT}`,
        contextOverride: trackingContext.form,
      });
    }
  }, [linkError]);

  const showEmbellishedVersion = !(window?.Jbhifi?.simpleMsg ?? false);

  const formatMobileNumber = number => `${number.substr(0, 4)} ${number.substr(4, 3)} ${number.substr(7)}`;

  const getMobileNumber = () => {
    if (data.receipt?.ReceiptData?.MobileNumber) {
      return formatMobileNumber(data.receipt.ReceiptData.MobileNumber.replace(' ', ''));
    } else if (data.receipt?.MobileNumber) {
      return formatMobileNumber(data.receipt.MobileNumber);
    }
    return '';
  };

  const verifyCode = async event => {
    event.preventDefault();
    tracker.trackClick({ label: 'Verify' });

    if (code?.length === 4) {
      setValidationError(null);
      setVerifying(true);

      const customerId = window.Jbhifi?.shopifyCustomerId;

      tracker.trackFormEvent({ label: 'Verify', action: formAction.submit });

      try {
        const response = await customerHelper.verifyCustomerMobile(customerId, code);

        if (response.isSuccess) {
          const klaviyoListIds = window.Jbhifi?.Receipts?.klaviyo_list_id_array ?? [];
          for (const listId of klaviyoListIds) {
            await onSubscribe(window.Jbhifi?.customerEmail, listId);
          }

          tracker.trackFormEvent({ action: formAction.complete });
          clearSession();
          receiptHelper.redirectToAccount(receiptId, { alert: 'mobileVerified' });
        } else if (response.isTooManyAttempts) {
          history.push('/lockedout');
        } else if (response.isNoMatch) {
          setValidationError(validationMessages.invalid);
          setVerifying(false);
        } else {
          setVerifying(false);
          setLinkError(true);
        }
      } catch (error) {
        setVerifying(false);
        setLinkError(true);
      }
    } else {
      const codeInput = document.querySelector('input[name="code_input"]');
      codeInput.focus();
      codeInput.blur();
      tracker.trackError({ errorMsg: validationMessages.required, contextOverride: trackingContext.form });
      setValidationError(validationMessages.required);
    }
  };

  const resendCode = () => {
    setResendSuccess(false);
    setResending(true);
    setCode(null);
    setLinkError(null);
    setValidationError(null);
    setCodeInputKey(codeInputKey + 1);

    tracker.trackClick({ label: 'resend' });

    const customerId = window.Jbhifi?.shopifyCustomerId;

    customerHelper
      .sendCustomerMobileVerification(customerId, receiptId)
      .then(result => {
        if (result.isSuccess) {
          setVerifyData(result);
          setResendSuccess(true);
        } else if (result.isTooManyAttempts) {
          history.push(`/lockedout`);
        }
        setResending(false);
        return result;
      })
      .catch(error => console.error(error, 'something went wrong with resending code'));
  };

  const handleChange = event => {
    setCode(event.target.value);
    if (event.target.value.length < 4) {
      setValidationError(validationMessages.required);
    } else {
      setValidationError(null);
    }
  };

  return (
    <ReceiptLayout ripped isLoading={isLoading}>
      {resendSuccess ? <JBAlert showIcon type="success" heading="Verification code sent" /> : null}
      {linkError ? <JBAlert showIcon type="error" heading={LINK_ERROR_HEADING} content={LINK_ERROR_CONTENT} /> : null}
      <div className="primary-block">
        {showEmbellishedVersion ? (
          <JBTypography align="center" type="h5" margin="0 0 25px">
            Claim your receipt by
            <br />
            entering the the verification
            <br />
            code we sent to
            <br />
            {getMobileNumber()}
          </JBTypography>
        ) : (
          <JBTypography align="center" type="h5" margin="0 0 25px">
            Enter the verification
            <br />
            code we sent to
            <br />
            {getMobileNumber()}
          </JBTypography>
        )}

        <form onSubmit={verifying ? event => event.preventDefault() : verifyCode}>
          <div ref={inputBlock} className="code-inputs">
            <JBCodeInput
              key={codeInputKey}
              name="code_input"
              autoFocus
              label="Enter 4-digit verification code"
              isRequired
              length={4}
              errorShow={validationError === validationMessages.invalid ? true : undefined}
              onChange={handleChange}
              errorMessage={validationError}
              inputMode="numeric"
              errorShowDelay={150}
            />
          </div>
          <JBTypography align="center" type="p" margin="10px 0 25px">
            <button type="button" className="link" onClick={resending ? null : resendCode}>
              {resending ? 'Resending' : 'Resend'}
            </button>
          </JBTypography>
          <JBButton
            type="primary"
            fullWidth={true}
            label={verifying ? 'Verifying...' : 'Verify'}
            onClick={verifying ? null : verifyCode}
            submit
          />
        </form>
      </div>
    </ReceiptLayout>
  );
};

export default VerifyView;
